import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { getWhatsappUrl } from "@/config/site";
import { servicesMegaMenu } from "@/config/servicesMenu";
import logo from '../../assets/logo_final.png';

const compactDesktopNav = [
  { name: "Home", to: "/" },
  { name: "About Us", to: "/about" },
  { name: "Services", to: "/services" },
  { name: "Gallery", to: "/gallery" },
  { name: "Blogs", to: "/blogs" },
  { name: "Contact", to: "/contact" },
];

const expandedDesktopNavPrimary = [
  { name: "Case Studies", to: "/case-studies" },
];

const expandedDesktopNavSecondary = [
  { name: "Safety Measures", to: "/safety-measures" },
  { name: "Technology", to: "/technology" },
  { name: "Our Team", to: "/our-team" },
  { name: "Published Papers", to: "/published-papers" },
];

const mobileNav = [
  { name: "Home", to: "/" },
  { name: "About", to: "/about" },
  { name: "Services", to: "/services" },
  { name: "Gallery", to: "/gallery" },
  { name: "Case Studies", to: "/case-studies" },
  { name: "Safety Measures", to: "/safety-measures" },
  { name: "Technology", to: "/technology" },
  { name: "Our Team", to: "/our-team" },
  { name: "Published Papers", to: "/published-papers" },
  { name: "Blogs", to: "/blogs" },
  { name: "Contact", to: "/contact" },
];

export default function SiteHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopHovered, setIsDesktopHovered] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 border-b border-[#f2e6c9] bg-white/85 backdrop-blur-lg"
      data-testid="global-header"
      onMouseEnter={() => setIsDesktopHovered(true)}
      onMouseLeave={() => setIsDesktopHovered(false)}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
        {/* LOGO */}
        <Link to="/" className="flex items-center" data-testid="brand-home-link">
          <img
            src={logo}
            alt="Kavlekar Dental logo"
            className="h-16 w-full object-cover"
            data-testid="brand-logo-image"
          />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden items-center gap-6 lg:flex" data-testid="desktop-compact-nav">
          {compactDesktopNav.map((item) => {

            /* ── SERVICES: button-only trigger + hover mega-dropdown ── */
            if (item.name === "Services") {
              return (
                <div
                  key={item.name}
                  className="group relative"
                  data-testid="compact-services-hover-dropdown-wrapper"
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm md:text-[0.92rem] tracking-wide font-[Manrope] text-[#333333] transition-colors hover:text-[#b08d45] group-hover:text-[#b08d45]"
                    data-testid="compact-nav-link-services"
                  >
                    Services
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                  </button>

                  <div className="absolute left-0 top-full h-3 w-full" />

                  <div
                    className="pointer-events-none absolute left-1/2 top-[calc(100%+12px)] z-50 w-[1100px] max-w-[95vw] -translate-x-1/2 rounded-2xl border border-[#f2e6c9] bg-white p-6 opacity-0 shadow-[0_18px_40px_rgba(0,0,0,0.1)] transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100"
                    data-testid="compact-services-hover-dropdown-panel"
                  >
                    <div className="grid grid-cols-6 gap-5">
                      {servicesMegaMenu.map((section) => (
                        <div
                          key={section.category}
                          className="space-y-2"
                          data-testid={`services-hover-section-${section.category.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <h4
                            className="font-[Playfair_Display] text-xl text-[#1a1a1a]"
                            data-testid={`services-hover-section-title-${section.category.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {section.category}
                          </h4>
                          <div className="space-y-1.5">
                            {section.items.map((service) => (
                              <NavLink
                                key={service.slug}
                                to={`/services/${service.slug}`}
                                className={({ isActive }) =>
                                  `block font-[Manrope] text-sm transition-colors ${
                                    isActive ? "text-[#b08d45]" : "text-[#444444] hover:text-[#b08d45]"
                                  }`
                                }
                                data-testid={`services-hover-item-${service.slug}`}
                              >
                                {service.title}
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            /* ── ALL OTHER NAV LINKS ── */
            return (
              <NavLink
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `text-sm md:text-[0.92rem] tracking-wide font-[Manrope] text-[#333333] transition-colors hover:text-[#b08d45] ${
                    isActive ? "text-[#b08d45]" : ""
                  }`
                }
                data-testid={`compact-nav-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        {/* DESKTOP WHATSAPP BUTTON */}
        <a
          href={getWhatsappUrl()}
          target="_blank"
          rel="noreferrer"
          className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 rounded-full font-[Manrope] text-[11px] xl:text-xs font-semibold uppercase tracking-widest text-[#3b1f00] shadow-md transition-all duration-200 hover:brightness-110 hover:shadow-lg active:scale-95"
          style={{
            background: "linear-gradient(#b8860b 0%)",
          }}
          data-testid="header-book-appointment-button"
        >
          Book With WhatsApp
        </a>

        {/* MOBILE MENU TOGGLE */}
        <button
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="rounded-md border border-[#f2e6c9] p-2 text-[#1a1a1a] lg:hidden"
          data-testid="mobile-menu-toggle-button"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ANIMATED GOLD DIVIDER */}
      <div
        className="h-px w-full animate-gold-sheen bg-gradient-to-r from-transparent via-[#e6ca89] to-transparent"
        data-testid="header-animated-gold-divider"
      />

      {/* DESKTOP EXPANDED NAV (on hover) */}
      <div
        className={`hidden overflow-hidden border-t border-[#f2e6c9] bg-white/95 transition-all duration-300 lg:block ${
          isDesktopHovered ? "max-h-52 opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="desktop-expanded-nav-wrapper"
      >
        <div className="mx-auto flex max-w-[50%] flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 md:px-10">
          {expandedDesktopNavPrimary.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              className={({ isActive }) =>
                `font-[Manrope] text-sm text-[#333333] transition-colors hover:text-[#b08d45] ${isActive ? "text-[#b08d45]" : ""}`
              }
              data-testid={`expanded-nav-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.name}
            </NavLink>
          ))}
          {expandedDesktopNavSecondary.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              className={({ isActive }) =>
                `font-[Manrope] text-sm text-[#333333] transition-colors hover:text-[#b08d45] ${isActive ? "text-[#b08d45]" : ""}`
              }
              data-testid={`expanded-nav-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.name}
            </NavLink>
          ))}
        </div>
      </div>

      {/* MOBILE MENU PANEL */}
      {isMobileMenuOpen && (
        <div
          className="space-y-3 border-t border-[#f2e6c9] bg-white px-6 py-4 lg:hidden"
          data-testid="mobile-menu-panel"
        >
          {mobileNav.map((item) => {
            if (item.name === "Services") {
              return (
                <div key={item.name} className="space-y-2">
                  <button
                    onClick={() => setIsMobileServicesOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 font-[Manrope] text-sm text-[#333333] hover:bg-[#fafafa]"
                  >
                    <span>Services</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${isMobileServicesOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isMobileServicesOpen && (
                    <div className="ml-3 space-y-3 border-l border-[#f2e6c9] pl-4">
                      {servicesMegaMenu.map((section) => (
                        <div key={section.category}>
                          <h4 className="font-semibold text-sm text-[#1a1a1a] mb-1">
                            {section.category}
                          </h4>
                          <div className="space-y-1">
                            {section.items.map((service) => (
                              <NavLink
                                key={service.slug}
                                to={`/services/${service.slug}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block text-sm text-[#444444] hover:text-[#b08d45] py-0.5"
                              >
                                {service.title}
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.name}
                to={item.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block rounded-lg px-2 py-2 font-[Manrope] text-sm ${
                    isActive ? "bg-[#f9f4e8] text-[#b08d45]" : "text-[#333333] hover:bg-[#fafafa]"
                  }`
                }
              >
                {item.name}
              </NavLink>
            );
          })}

          {/* MOBILE WHATSAPP BUTTON */}
          <a
            href={getWhatsappUrl()}
            target="_blank"
            rel="noreferrer"
            className="flex w-full max-w-[280px] mx-auto items-center justify-center rounded-full px-6 py-3 font-[Manrope] text-sm font-semibold uppercase tracking-widest text-[#3b1f00] shadow-md transition-all duration-200 hover:brightness-110 active:scale-95"
            style={{
              background: "linear-gradient(#b8860b 0%)",
            }}
            data-testid="mobile-book-appointment-button"
          >
            Book With WhatsApp
          </a>
        </div>
      )}
    </header>
  );
}